import React, { useEffect } from 'react';
import { Modal, Typography, Card, Tag, Space, Row, Col, Timeline, Divider, Empty, Button, message } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  VideoCameraOutlined, 
  GlobalOutlined, 
  HomeOutlined, 
  QuestionOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  EditOutlined,
  SwapOutlined,
  CheckOutlined, 
  CloseOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';

const { Title, Text } = Typography;

const BranchDetailsModal = ({ visible, onClose, classData, teacherData, classChangeLogs, onRefresh }) => {
  const { t } = useTranslation();
  const [studentConfirmed, setStudentConfirmed] = React.useState(!!classData?.student_confirm);
const [confirming, setConfirming] = React.useState(false);

React.useEffect(() => {
  setStudentConfirmed(!!classData?.student_confirm);
}, [classData?.classid, classData?.student_confirm]);

  // Get teacher name
  const getTeacherName = () => {
    if (!teacherData) return '-';
    return `${teacherData.nickname || ''} ${teacherData.firstname || ''} ${teacherData.lastname || ''}`.trim();
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (!status) return <Tag color="default">No Status</Tag>;
    
    switch(status.toLowerCase()) {
      case 'approved':
        return <Tag color="green">Approved</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'cancelled':
        return <Tag color="red">Cancelled</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };


const handleStudentConfirm = async (confirm = true) => {
  if (!classData?.classid) {
    console.warn('[student-confirm] Missing classid in classData:', classData);
    return;
  }

  // Add confirmation dialog here
  const confirmed = await new Promise((resolve) => {
    Modal.confirm({
      title: confirm ? t('Confirm Student') : t('Unconfirm Student'),
      content: confirm 
        ? t('Are you sure you want to confirm this student?')
        : t('Are you sure you want to unconfirm this student?'),
      okText: t('Yes'),
      cancelText: t('No'),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  // If user clicked "No" or closed the modal, exit early
  if (!confirmed) {
    return;
  }

  setConfirming(true);

  try {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL ?? ''}/classes/${classData.classid}/student-confirm`.replace(/(?<!:)\/{2,}/g, '/'); 
    const payload = { confirm };
    const headers = { Authorization: `Bearer ${token}` };

    const res = await axios.put(url, payload, { headers });

    setStudentConfirmed(!!confirm);
    message.success(confirm ? (t('studentConfirmedSuccessfully') || 'Student confirmed')
                            : (t('studentUnconfirmedSuccessfully') || 'Student unconfirmed'));

    if (onRefresh && typeof onRefresh === 'function') {
      await onRefresh();
    }
  } catch (err) {
    // Rich diagnostics
    console.error('[student-confirm] Error', {
      message: err?.message,
      code: err?.code,
      status: err?.response?.status,
      statusText: err?.response?.statusText,
      responseDataPreview: typeof err?.response?.data === 'string'
        ? err.response.data.slice(0, 400)
        : err?.response?.data,
      requestConfig: {
        url: err?.config?.url,
        method: err?.config?.method,
        baseURL: err?.config?.baseURL,
        data: err?.config?.data,
        headers: err?.config?.headers,
      }
    });

    message.error(
      err?.response?.data?.message
        || (confirm ? 'Already confirmed or class not found'
                    : 'Already unconfirmed or class not found')
    );
  } finally {
    setConfirming(false);
  }
};


  const formatPlatform = (platform) => {
    switch (platform) {
      case 'online':
        return (
          <Space>
            <VideoCameraOutlined style={{ color: '#1890ff' }} /> {t('online')}
          </Space>
        );
      case 'onsite':
        return (
          <Space>
            <GlobalOutlined style={{ color: '#52c41a' }} /> {t('onsite')}
          </Space>
        );
      case 'athome':
        return (
          <Space>
            <HomeOutlined style={{ color: '#fa8c16' }} /> {t('athome')}
          </Space>
        );
      default:
        return (
          <Space>
            <QuestionOutlined style={{ color: '#d9d9d9' }} /> -
          </Space>
        );
    }
  };

  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD MMM YYYY');
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format time only
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    try {
      const time = moment(timeString, 'HH:mm:ss');
      if (time.isValid()) {
        return time.format('HH:mm');
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to "2:30hrs" format
  const formatDuration = (durationString) => {
    if (!durationString) return '-';
    
    try {
      // Handle format like "2h30m" or "2 30"
      if (durationString.includes('h') && durationString.includes('m')) {
        const hours = parseInt(durationString.split('h')[0]);
        const minutes = parseInt(durationString.split('h')[1].replace('m', ''));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Handle format like "2 30" (hours and minutes with space)
      if (durationString.includes(' ')) {
        const [hours, minutes] = durationString.split(' ').map(part => parseInt(part));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Try to convert minutes to hours:minutes format
      const totalMinutes = parseInt(durationString);
      if (!isNaN(totalMinutes)) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // If we can't parse it, return as is
      return durationString;
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString || '-';
    }
  };

  // Format change log timestamp
  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return moment(timestamp).format('DD MMM YYYY HH:mm');
    } catch (error) {
      return timestamp;
    }
  };

  // Filter class change logs for current class
  const getFilteredClassChangeLogs = () => {
    if (!classChangeLogs || !classData?.classid) return [];
    return classChangeLogs.filter(log => log.classid === classData.classid);
  };

  // Get change type icon
  const getChangeIcon = (changeType) => {
    switch(changeType?.toLowerCase()) {
      case 'reschedule':
      case 'time_change':
        return <ClockCircleOutlined style={{ color: '#fa8c16' }} />;
      case 'teacher_change':
        return <UserOutlined style={{ color: '#1890ff' }} />;
      case 'platform_change':
        return <SwapOutlined style={{ color: '#52c41a' }} />;
      case 'status_change':
        return <EditOutlined style={{ color: '#722ed1' }} />;
      default:
        return <EditOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Get change description based on the log data
  const getChangeDescription = (log) => {
    if (log.changereason) return log.changereason;
    if (log.note) return log.note;
    
    // Generate description based on what changed
    const changes = [];
    if (log.originaldate !== log.newdate) {
      changes.push(`Date changed from ${formatDate(log.originaldate)} to ${formatDate(log.newdate)}`);
    }
    if (log.originalstarttime !== log.newstarttime) {
      changes.push(`Start time changed from ${formatTime(log.originalstarttime)} to ${formatTime(log.newstarttime)}`);
    }
    if (log.originalendtime !== log.newendtime) {
      changes.push(`End time changed from ${formatTime(log.originalendtime)} to ${formatTime(log.newendtime)}`);
    }
    
    return changes.length > 0 ? changes.join(', ') : 'Class details updated';
  };

  const InfoCard = ({ icon, title, value, color = '#1890ff' }) => (
    <Card 
      size="small" 
      style={{ 
        height: '100%',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space>
          {React.cloneElement(icon, { style: { color, fontSize: '16px' } })}
          <Text strong style={{ color: '#595959' }}>{title}</Text>
        </Space>
        <div style={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>
          {value}
        </div>
      </Space>
    </Card>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      {classData ? (
        <div>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
              {classData.subject_name}
            </Title>
            <Space size="large">
              {getStatusTag(classData.status)}
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Class ID: {classData.classid}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Course: {classData.class_code || '-'}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Branch: {classData.name || '-'}
              </Text>
            </Space>
            <Space size="small" style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                <Tag color={studentConfirmed ? 'green' : 'default'}>
                  {studentConfirmed ? (t('Confirmed') || 'Student Confirmed') : (t('Not Confirmed') || 'Student Not Confirmed')}
                </Tag>
              <Button
                type="primary"
                shape="circle"
                icon={<CheckOutlined />}
                onClick={() => handleStudentConfirm(true)}
                loading={confirming}
                disabled={confirming || studentConfirmed}
              />

              <Button
                danger
                shape="circle"
                icon={<CloseOutlined />}
                onClick={() => handleStudentConfirm(false)}
                loading={confirming}
                disabled={confirming || !studentConfirmed}
              />
              </Space>

          </div>

          {/* Class Details Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<UserOutlined />}
                title="TEACHER"
                value={getTeacherName()}
                color="#722ed1"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<CalendarOutlined />}
                title="DATE"
                value={formatDate(classData.date)}
                color="#52c41a"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<ClockCircleOutlined />}
                title="TIME"
                value={`${formatTime(classData.schedule_time)} - ${formatTime(classData.end_time)}`}
                color="#fa8c16"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<BookOutlined />}
                title="DURATION"
                value={formatDuration(classData.schedule_hour || classData.duration)}
                color="#13c2c2"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<VideoCameraOutlined />}
                title="PLATFORM"
                value={formatPlatform(classData.platform)}
                color="#1890ff"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<EnvironmentOutlined />}
                title="CLASSROOM"
                value={classData.classroom_number || '-'}
                color="#f5222d"
              />
            </Col>
          </Row>

             {classData.book_details_materials && (
                      <Card
                        size="small"
                        style={{
                          marginBottom: '24px',
                          backgroundColor: '#fffbe6',
                          borderLeft: '4px solid #faad14',
                        }}
                      >
                        <Space direction="vertical" size="small">
                          <Text strong style={{ fontSize: '14px' }}>
                            <EditOutlined style={{ marginRight: 8 }} />
                            Note
                          </Text>
                          <Text style={{ whiteSpace: 'pre-line' }}>{classData.book_details_materials}</Text>
                        </Space>
                      </Card>
                    )}

        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Empty description="No class details available" />
        </div>
      )}
    </Modal>
  );
};

export default BranchDetailsModal;